// Componente per la scena 3D
import { OrbitControls, Stage, MeshReflectorMaterial } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { useRef, useEffect } from "react";
// import { useControls } from 'leva';
// import { Perf } from "r3f-perf";

export default function Scene({
    children,              // Componenti figli (modello)
    error,                 // Stato di errore del modello
    isMobileView,          // Flag per la vista mobile
    mobileFov,             // FOV per la vista mobile
    sceneParams,           // Parametri della scena (FOV, ombre, ecc.)
    renderingStatus,        // Flag per il rendering
}) {
    const LIGHT_INTESITY_FACTOR = 0.15;

    // Hook per accedere alla camera di Three.js
    const { camera } = useThree();

    // Ref per i controlli orbitali
    const orbitControlsRef = useRef();

    // Leva controls for the reflector material
    // const configReflect = useControls(
    //     {
    //         mixBlur:{value: 0.5, min:0, max:100, step: 0.01}, // How much blur mixes with surface roughness (default = 1)
    //         mixStrength: {value: 500, min:0, max:1000, step: 0.01}, // Strength of the reflections
    //         mixContrast: {value: 1, min:0, max:1, step: 0.01}, // Contrast of the reflections
    //         mirror: {value: 0.3, min:0, max:1, step: 0.01}, // Mirror environment, 0 = texture colors, 1 = pick up env colors
    //         depthScale:{value: 15, min: 0, max: 10, step: 0.01}, // Scale the depth factor (0 = no depth, default = 0)
    //         minDepthThreshold:{value: 0.8, min: 0, max: 100, step: 0.01}, // Lower edge for the depthTexture interpolation (default = 0)
    //         maxDepthThreshold:{value: 14, min: 0, max: 100, step: 0.01}, // Upper edge for the depthTexture interpolation (default = 0)
    //         depthToBlurRatioBias:{value: 0.25, min: 0, max: 1, step: 0.01}, // Adds a bias factor to the depthTexture before calculating the blur amount [blurFactor = blurTexture * (depthTexture + bias)]. It accepts values between 0 and 1, default is 0.25. An amount > 0 of bias makes sure that the blurTexture is not too sharp because of the multiplication with the depthTexture
    //         // distortion={1} // Amount of distortion based on the distortionMap texture
    //         //distortionMap={distortionTexture} // The red channel of this texture is used as the distortion map. Default is null
    //         reflectorOffset:{value: 0, min: 0, max: 10, step: 0.01}, // Offsets the virtual camera that projects the reflection. Useful when the reflective surface is some distance from the object's origin (default = 0)
    //         transparent: {value: true, label: 'Transparent'},
    //         opacity: { value: 0.2, min: 0, max: 1, step: 0.01 },
    //         metalness: {value: 0, min: 0, max: 1, step: 0.01},
    //         roughness: {value: 0.8, min: 0, max: 1, step: 0.01},
    //         color: {value: "#606060", label: 'Color'}, // Keep it dark, to avoid the noise on the reflected lights
    //     }
    // );

    // Estrai i parametri dalla scena
    const reflect = sceneParams?.useReflect;    // Flag per il piano riflettente
    // const debugMode = sceneParams?.debugMode;   // Flag per la modalità debug

    // Configurazione default per i controlli orbitali
    const orbitControlsDefault = {
        minDistance: 0.125,     // Distanza minima di zoom
        maxDistance: 0.65,      // Distanza massima di zoom
        target: [0, 0.019, 0]   // Punto di focus della camera
    }

    // Effetto per gestire il ridimensionamento della finestra
    useEffect(() => {
        const handleResize = () => {
            // Aggiorna il FOV in base alla larghezza della finestra
            const newFov = window.innerWidth <= 700 ? mobileFov : sceneParams.fieldOfView;
            camera.fov = newFov;
            camera.updateProjectionMatrix();
        };

        // Aggiungi e rimuovi l'event listener per il ridimensionamento
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [camera]);

    // Effetto per aggiornare i controlli orbitali quando cambia il modello
    useEffect(() => {
        if (orbitControlsRef.current) {
            const controls = orbitControlsRef.current;

            // Applica le proprietà di default
            controls.minDistance = orbitControlsDefault.minDistance;
            controls.maxDistance = orbitControlsDefault.maxDistance;

            // Imposta il punto di focus
            controls.target.set(...orbitControlsDefault.target);

            // Forza l'aggiornamento dei controlli
            controls.reset();
            controls.update();
        }
    }, [sceneParams.src]);

    return (
        <>
            {/* Visualizzatore delle performance (solo in modalità debug) */}
            {/* {debugMode && <Perf position="top-left" />} */}

            {/* Controlli orbitali per la navigazione della camera */}
            <OrbitControls
                ref={orbitControlsRef}
                enableZoom={!sceneParams?.disableZoom}    // Abilita/disabilita lo zoom
                enablePan={false}                        // Disabilita il pan
                autoRotate={!isMobileView ? false : sceneParams?.autoRotate}  // Rotazione automatica su mobile
                {...orbitControlsDefault}                // Applica le impostazioni default
                makeDefault                             // Rendi questi controlli i default
            />

            {/* Visualizzazione della scena 3D */}
            {!error && sceneParams.src && (
                <Stage
                    adjustCamera={false}                 // Non modificare la camera
                    shadows={sceneParams.useNoShadows ? false : { // Configurazione delle ombre
                        type: 'contact',
                        intensity: sceneParams.shadowIntensity,
                        smooth: sceneParams.shadowSmooth,
                    }}
                    preset='rembrandt'                       // Preset per l'illuminazione
                    environment={sceneParams.hdr ? {    // Configurazione dell'ambiente
                        files: sceneParams.hdr,
                        environmentIntensity: sceneParams.lightExposition - LIGHT_INTESITY_FACTOR,
                        environmentRotation: [0, Math.PI / 2, 0],
                    } : {
                        preset: 'apartment',
                        environmentIntensity: 0.9,
                        // environmentRotation: [Math.PI / 4, Math.PI / 4, Math.PI / 4],
                    }}
                    intensity={0.1} // Intensità dell'illuminazione, default 0.5
                >
                    {/* Caricamento del modello 3D */}
                    {children}
                </Stage>
            )}

            {/* Piano riflettente (opzionale) */}
            {reflect && (
                <>
                    {!sceneParams.backgroundImage && <color attach="background" args={[sceneParams.backgroundColor]}/>}
                    <mesh
                        position={[0, sceneParams.modelMinY - 0.0005, 0]} // TODO: to calculate the position based to the model
                        rotation={[-Math.PI / 2, 0, 0]}
                        receiveShadow
                    >
                        <planeGeometry args={[10, 10]} />
                        <MeshReflectorMaterial
                            blur={[0, 0]} // Blur ground reflections (width, height), 0 skips blur
                            mixBlur={0.01} // How much blur mixes with surface roughness (default = 1)
                            mixStrength={2048} // Strength of the reflections
                            mixContrast={1} // Contrast of the reflections
                            resolution={2048} // Off-buffer resolution, lower=faster, higher=better quality, slower
                            mirror={0} // Mirror environment, 0 = texture colors, 1 = pick up env colors
                            depthScale={renderingStatus ? 0.1 : 0} // Scale the depth factor (0 = no depth, default = 0)
                            minDepthThreshold={0.8} // Lower edge for the depthTexture interpolation (default = 0)
                            maxDepthThreshold={0.91} // Upper edge for the depthTexture interpolation (default = 0)
                            depthToBlurRatioBias={0.05} // Adds a bias factor to the depthTexture before calculating the blur amount [blurFactor = blurTexture * (depthTexture + bias)]. It accepts values between 0 and 1, default is 0.25. An amount > 0 of bias makes sure that the blurTexture is not too sharp because of the multiplication with the depthTexture
                            reflectorOffset={0.001} // Offsets the virtual camera that projects the reflection. Useful when the reflective surface is some distance from the object's origin (default = 0)
                            transparent
                            opacity={0.1}
                            metalness={0}
                            roughness={0.72}
                            color="#030303"
                            depthWrite={false}
                            depthTest
                            renderOrder={-1}
                        />
                    </mesh>
                    {/* <fog attach="fog" args={[sceneParams.backgroundColor, 0.8, 1.5]} /> */}
                </>
            )}

            {/* Luce ambientale per l'illuminazione base */}
            {/* <ambientLight intensity={0.5} /> */}
        </>
    );
};